import create from "zustand";
import { persist } from "zustand/middleware";
import { findUnixExpiryFromPeriods, getClassSessionType, getClassStartTimeUnix } from "@/js/utils/timeUtils";
import { ClassroomDetails, ClassSessionDTO, ZoomSchedule } from "@/stores/classroomStore_I";
import md5 from "md5";
import moment from "moment";
import { reportScreenshotReliability } from "@/js/api/screenshotIssueApi";
import { User } from "@/js/utils/userUtils";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useClassroomScreenshotDataStore } from "@/stores/v2/classroomScreenshotDataStore";
import { getClassroomStore } from "@/stores/classroomStore";
import useStore from "@/stores";

export const STUDENT_STATE_STORE_KEY = "student-state-store";

export enum StudentScreenshotStatus {
	// Default status when no action has been taken.
	CONNECTING = "CONNECTING",
	// When the connecting time has elapsed, and no screenshot has been received.
	NO_ACTIVITY = "NO_ACTIVITY",
	// When the student has activity
	ACTIVE = "ACTIVE",
	// When a screenshot was previously available, but has since expired
	DISCONNECTED = "DISCONNECTED",
	// Status to be set when the teacher clicks the "Retry connecting" button.
	RETRIED = "RETRIED",
	// Status to be set when the teacher clicks the "Still having issues" button.
	ISSUE_ONGOING = "ONGOING",
	// Status to be set when the student is in RETRIED or ISSUE_ONGOING status, and the screenshot subsequently arrives.
	RESOLVED = "RESOLVED",
}

const REPORTABLE_SCREENSHOT_STATUSES = [
	StudentScreenshotStatus.RETRIED,
	StudentScreenshotStatus.ISSUE_ONGOING,
	StudentScreenshotStatus.RESOLVED,
];

interface ClassSessionStudentStates {
	[classSessionId: string]: {
		[studentStableId: string]: StudentScreenshotStatus;
	};
}

interface State {
	classSessions: ClassSessionStudentStates;
}

interface Actions {
	setScreenshotStatus: (student: User, classroomId: string, status: StudentScreenshotStatus, classOpenTime: number) => Promise<void>;
	getScreenshotStatus: (studentStableId: string, classroomId: string) => StudentScreenshotStatus | undefined;
}

export const useStudentState = create<State & Actions>(
	persist(
		(set, get) => ({
			classSessions: {},

			setScreenshotStatus: async (student: User, classroomId, status, classOpenTime) => {
				const classroom = getClassroomStore(useStore.getState()).getClassroomV2(classroomId);
				const sessionUniqueId = HackyGetClassroomSessionId(classroom);

				const selectedClassroom = useSelectedClassroomStore.getState();
				const windowStore = useClassroomScreenshotDataStore.getState();

				const studentScreenshotTuples = selectedClassroom.students?.map((s) => [s.username, windowStore.getWindowDataForUser(s.username)]);
				const studentsWithScreenshots = studentScreenshotTuples?.filter(([, windowData]) => !!windowData);
				const studentsWithoutScreenshots = studentScreenshotTuples
					?.filter(([, windowData]) => !windowData)
					.map(([username]) => username as string);

				const classSession = get().classSessions[sessionUniqueId] || {};

				if (classSession) {
					const studentCurrentStatus = classSession[student.stableIdV1];
					const classStartTime = getClassStartTimeUnix(classroom);
					const classSessionType = getClassSessionType(classroom);

					// if the status has changed, and is not NONE...
					if (studentCurrentStatus !== status && REPORTABLE_SCREENSHOT_STATUSES.includes(status) && classStartTime && classSessionType) {
						await reportScreenshotReliability({
							studentUsername: student.username,
							studentStableId: student.stableIdV1,
							classroomSourceName: classroom.sourceName ?? classroom.name,
							classroomStableId: classroom.stableIdV1,
							classStartTime: classStartTime,
							classSessionType: classSessionType,
							classLoadedTime: classOpenTime,
							remediationType: status,
							totalStudentsInClass: selectedClassroom.students?.length ?? 0,
							totalStudentsWithScreenshots: studentsWithScreenshots?.length ?? 0,
							studentsNotShowingScreenshots: studentsWithoutScreenshots ?? [],
						});
					}
				}

				set((state) => {
					const classSession = state.classSessions[sessionUniqueId] || {};
					return {
						classSessions: {
							...state.classSessions,
							[sessionUniqueId]: {
								...classSession,
								[student.stableIdV1]: status,
							},
						},
					};
				});
			},

			getScreenshotStatus: (studentStableId, classroomID: string) => {
				const classroom = getClassroomStore(useStore.getState()).getClassroomV2(classroomID);
				const sessionUniqueId = HackyGetClassroomSessionId(classroom);
				return get().classSessions[sessionUniqueId]?.[studentStableId] ?? StudentScreenshotStatus.CONNECTING;
			},
		}),
		{
			getStorage: () => sessionStorage,
			name: STUDENT_STATE_STORE_KEY,
		}
	)
);

/**
 * Generates a unique session ID for a classroom session based on the classroom ID, schedule, and session details.
 * For schedule classes, each instance of a period will have a unique ID based on the current date.
 * (i.e. a class that runs every Monday at 10am will have a unique ID for each Monday).
 *
 * The session ID is generated as follows:
 * - Start with the classroom ID.
 * - If the session is a monitoring session, append "_monitoring_" and the rounded session start time (to the nearest 5 minutes).
 * - If the session is a regular session and has a session start time, append "_regular_" and the rounded session start time.
 * - If the session is a scheduled session, append "_regular_" and the expiry time from the schedule periods,
 * 		plus the current date (to distinguish from other instances of the same period on different days).
 *
 * This is a little hacky, but required until the backend provides a unique session ID for each class session, which
 * should be coming soon.
 *
 * WARNING: Please do not use this for anything that needs to be persisted permanently, this is simply a frontend hack
 * to get around the lack of a proper session ID from the backend.
 * @param classroomDetails - The classroom to generate the session ID for.
 * @deprecated - DO NOT USE. Temporary until ID is generated on the backend.
 */
export const HackyGetClassroomSessionId = (classroomDetails: ClassroomDetails): string => {
	const classroomId = classroomDetails.id;

	if (!classroomId) {
		console.warn("HackyGetClassroomSessionId: No classroom ID provided.");
		return "";
	}

	const schedule = classroomDetails?.schedule as ZoomSchedule | undefined;
	const session = classroomDetails?.session as ClassSessionDTO | undefined;

	const sessionStartTime = session?.sessionStartTime;
	// Round the timestamp to the nearest 5 minutes, as the "session start time" from the backend and generated in the
	// frontend may be off by a little bit.
	const roundedTimestamp = sessionStartTime
		? moment(sessionStartTime)
				.startOf("minute")
				.add(5 - (moment(sessionStartTime).minute() % 5), "minutes")
				.valueOf()
		: undefined;

	const isMonitoring = session?.isMonitoring;
	let sessionString = `${classroomId}`;

	if (isMonitoring) {
		sessionString += `_monitoring_${roundedTimestamp}_${sessionStartTime}`;
	} else {
		sessionString += "_regular";
		if (sessionStartTime) {
			sessionString += `_${roundedTimestamp}_${sessionStartTime}`;
		} else {
			let expiryUnix = null;
			if (schedule && schedule.periods) {
				expiryUnix = findUnixExpiryFromPeriods(schedule.periods);
				const currentDate = new Date().toDateString();
				sessionString += `_${expiryUnix}_${currentDate}`;
			}
		}
	}
	return md5(sessionString);
};
